import ConsoleApi from "apps/console/enums/consoleApi";
import { QueryClient, UseMutationResult, useQueryClient, UseQueryResult } from "react-query";
import { useCopies } from "shared/hooks/useCopies";
import { ApiProgramResponse } from "shared/types/ApiProgramResponse";
import { successToast } from "shared/utils/toastNotification";
import { getRegDetailsKey } from "shared/context/regDetail/dataloader";
import { ApiRegistrationEvent, ApiRegistrationProgram } from "shared/types/ApiRegistration";
import { useEntity } from "apps/console/context/entity";
import { useGetApiData, usePostApiData } from "shared/datalayer";
import ApiField from "../../../../shared/types/ApiField";

export function useGetProgram({ programId }: { programId?: string }): UseQueryResult<ApiProgramResponse> {
  return useGetApiData<ApiProgramResponse>(
    ConsoleApi.GET_PROGRAM_BY_ID,
    {
      pathParams: { id: programId }
    },
    {
      enabled: Boolean(programId)
    }
  );
}

export function useCreateInPersonMeetup(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.CREATE_IN_PERSON_MEETUP_PROGRAM, {
    onSuccess: () => {
      successToast(copies.get("program.create.success.msg"));
    }
  });
}

export function useCreateMeetupProgram() {
  const copies = useCopies();
  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.CREATE_MEETUP_PROGRAM, {
    onSuccess: () => {
      successToast(copies.get("program.create.success.msg"));
    }
  });
}

export function useCreateTabletalk(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.CREATE_TABLETALK_PROGRAM, {
    onSuccess: () => {
      successToast(copies.get("program.create.success.msg"));
    }
  });
}

export function useCreateAssociatedProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.CREATE_ASSOCIATED_PROGRAM, {
    onSuccess: () => {
      successToast(copies.get("program.create.success.msg"));
    }
  });
}

export function useCreateBuyersProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.CREATE_BUYERS_PROGRAM, {
    onSuccess: () => {
      successToast(copies.get("program.create.success.msg"));
    }
  });
}

export function useCreateTradeProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.CREATE_TRADE_PROGRAM, {
    onSuccess: () => {
      successToast(copies.get("program.create.success.msg"));
    }
  });
}

export function useCreateConnectionProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.CREATE_CONNECTION_PROGRAM, {
    onSuccess: () => {
      successToast(copies.get("program.create.success.msg"));
    }
  });
}

export function useUpdateInPersonMeetup(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const { registrationId } = useEntity();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.UPDATE_IN_PERSON_MEETUP_PROGRAM, {
    onSuccess: data => {
      successToast(copies.get("program.update.success.msg"));
      updateProgramInRegDetails({ data, queryClient, registrationId });
    }
  });
}

export function useUpdateMeetup(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const { registrationId } = useEntity();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.UPDATE_MEETUP_PROGRAM, {
    onSuccess: data => {
      successToast(copies.get("program.update.success.msg"));
      updateProgramInRegDetails({ data, queryClient, registrationId });
    }
  });
}

export function useUpdateTabletalk(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const { registrationId } = useEntity();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.UPDATE_TABLETALK_PROGRAM, {
    onSuccess: data => {
      successToast(copies.get("program.update.success.msg"));
      updateProgramInRegDetails({ data, queryClient, registrationId });
    }
  });
}

export function useUpdateBuyersProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const { registrationId } = useEntity();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.UPDATE_BUYERS_PROGRAM, {
    onSuccess: data => {
      successToast(copies.get("program.update.success.msg"));
      updateProgramInRegDetails({ data, queryClient, registrationId });
    }
  });
}

export function useUpdateTradeProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const { registrationId } = useEntity();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.UPDATE_TRADE_PROGRAM, {
    onSuccess: data => {
      successToast(copies.get("program.update.success.msg"));
      updateProgramInRegDetails({ data, queryClient, registrationId });
    }
  });
}

export function useUpdateAssociatedProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const { registrationId } = useEntity();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.UPDATE_ASSOCIATED_PROGRAM, {
    onSuccess: data => {
      successToast(copies.get("program.update.success.msg"));
      updateProgramInRegDetails({ data, queryClient, registrationId });
    }
  });
}

export function useUpdateConnectionProgram(): UseMutationResult<ApiProgramResponse> {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const { registrationId } = useEntity();

  return usePostApiData<ApiProgramResponse, any>(ConsoleApi.UPDATE_CONNECTION_PROGRAM, {
    onSuccess: data => {
      successToast(copies.get("program.update.success.msg"));
      updateProgramInRegDetails({ data, queryClient, registrationId });
    }
  });
}

export function updateProgramInRegDetails({
  data,
  queryClient,
  registrationId
}: {
  registrationId: string;
  queryClient: QueryClient;
  data: ApiProgramResponse;
}) {
  queryClient.setQueryData(getRegDetailsKey({ registrationId }).key, (reg: any) => {
    return {
      ...reg,
      events: reg.events.map((event: ApiRegistrationEvent) => ({
        ...event,
        programs: event.programs.map((program: ApiRegistrationProgram) =>
          program.id === data.id
            ? {
                ...program,
                name: data.name,
                startDate: data.startDate,
                participationLocationType: data.participationLocationType
              }
            : program
        )
      }))
    };
  });
}

export function useGetRegistrationFieldsForConfig(registrationId: string) {
  return useGetApiData<Array<ApiField>>(
    ConsoleApi.GET_REGISTRATION_FIELDS_FOR_CONFIG,
    { params: { registrationId } },
    { enabled: Boolean(registrationId) }
  );
}
